import { AutoAwesomeOutlined } from "@mui/icons-material";
import { Box, Paper, Stack, Typography } from "@mui/material";
import { formatShortDate } from "components/Admin/Organizations/OrganizationDetail/OrganizationClientBrain/clientBrainUtils";
import { SubscriptionCadence } from "components/Conversations/Views/Drawer/components/Subscriptions/helpers";
import AcxButton from "components/UI/AcxButton";
import AcxChip from "components/UI/AcxChip";
import AcxToggleButton from "components/UI/AcxToggleButton";
import React from "react";
import { KboFlowSubscription } from "services/KboFlowSubscriptionsService";

const cadenceOptions = [
    {
        id: "kbo-flow-user-subscription-weekly",
        value: SubscriptionCadence.Weekly,
        element: "Weekly",
    },
    {
        id: "kbo-flow-user-subscription-monthly",
        value: SubscriptionCadence.Monthly,
        element: "Monthly",
    },
];

type Props = {
    subscription: KboFlowSubscription | null;
    saving: boolean;
    /** Subscribes (or re-subscribes) the caller at the given cadence. */
    onSubscribe: (cadence: SubscriptionCadence) => void;
    onTurnOff: () => void;
};

/**
 * The caller's built-in KBo Flow (discovery) subscription, where KBo picks what to research.
 * Unlike question subscriptions there is at most one per user, so it is turned on and off in
 * place rather than added and deleted.
 */
export function KboFlowDiscoverySubscriptionCard({
    subscription,
    saving,
    onSubscribe,
    onTurnOff,
}: Props) {
    // While turned off, the frequency picker only chooses what "Turn on" will use.
    const [offCadence, setOffCadence] = React.useState(
        SubscriptionCadence.Weekly,
    );
    const cadence = subscription?.cadence ?? offCadence;

    return (
        <Paper
            id="kbo-flow-user-subscription"
            variant="outlined"
            sx={{
                padding: "16px 16px 14px",
                borderRadius: "8px",
                borderColor: "#E5E7EB",
            }}
        >
            <Stack rowGap={1.25}>
                <Stack direction="row" alignItems="center" columnGap={1.5}>
                    <Box
                        sx={{
                            width: 32,
                            height: 32,
                            flexShrink: 0,
                            borderRadius: "8px",
                            display: "grid",
                            placeItems: "center",
                            background:
                                "linear-gradient(135deg, #3564D5 0%, #349AC7 55%, #32D1B7 100%)",
                            color: "#FFFFFF",
                        }}
                    >
                        <AutoAwesomeOutlined sx={{ fontSize: 18 }} />
                    </Box>
                    <Typography fontWeight={600}>KBo Flow</Typography>
                    <AcxChip
                        size="small"
                        color={subscription ? "blue" : "gray"}
                        label={subscription ? "Subscribed" : "Off"}
                    />
                    {subscription && (
                        <Typography
                            sx={{
                                marginLeft: "auto",
                                fontSize: 13,
                                color: "#6B7280",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {`Last updated ${formatShortDate(
                                subscription.modifiedOn,
                            )}`}
                        </Typography>
                    )}
                </Stack>

                <Typography
                    sx={{ fontSize: 13, lineHeight: "19px", color: "#4B5563" }}
                >
                    KBo keeps researching after you close the chat and emails
                    you what changed in your program, the conversations behind
                    it, and what to do next.
                </Typography>

                <Stack
                    direction="row"
                    alignItems="center"
                    columnGap={1.25}
                    flexWrap="wrap"
                    rowGap={1}
                >
                    <Typography fontWeight={500}>Send it</Typography>
                    <AcxToggleButton
                        id="user-settings-subscription-cadence"
                        defaultValue={cadence}
                        exclusive
                        items={cadenceOptions}
                        disabled={saving}
                        onChange={(value) => {
                            if (value === null || value === cadence) return;
                            if (subscription) {
                                onSubscribe(value as SubscriptionCadence);
                            } else {
                                setOffCadence(value as SubscriptionCadence);
                            }
                        }}
                    />
                    <Box flex={1} />
                    <AcxButton
                        id={
                            subscription
                                ? "kbo-flow-user-subscription-turn-off"
                                : "kbo-flow-user-subscription-turn-on"
                        }
                        color="white"
                        variant="outlined"
                        loading={saving}
                        onClick={() =>
                            subscription ? onTurnOff() : onSubscribe(cadence)
                        }
                    >
                        {subscription ? "Turn off" : "Turn on"}
                    </AcxButton>
                </Stack>
            </Stack>
        </Paper>
    );
}
